import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const fmt = (n) => `\u20B9${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dt = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

/**
 * Read-only breakdown popup for the Income / Expense bands on the Balance Sheet.
 *
 * Props:
 *   kind: 'income' | 'expense'
 *   summary: the `/finance/summary` payload (already in memory on the parent
 *     — saves a roundtrip and keeps the totals in lockstep with the band).
 *   fromDate / toDate: same period the band is showing.
 *
 * Income popup contents:
 *   - Billed Service Charge total (one synthetic row — derived from invoices).
 *   - Individual non-`Invoice Payment` income entries.
 * Expense popup contents:
 *   - All expense entries in the period.
 */
export default function IncomeExpenseDialog({ open, onOpenChange, kind, summary, fromDate, toDate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = { type: kind };
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    api.get('/finance', { params })
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : [];
        // For income, hide the auto-posted `Invoice Payment` rows — the
        // service-charge portion of those is already represented by the
        // synthetic "Billed Service Charge" row pulled from the summary.
        setRows(kind === 'income' ? data.filter(t => t.category !== 'Invoice Payment') : data);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, kind, fromDate, toDate]);

  const isIncome = kind === 'income';
  const entriesTotal = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const svcCharge = isIncome ? (summary?.billed_svc_charge || 0) : 0;
  const grandTotal = entriesTotal + svcCharge;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-2xl" data-testid={`${kind}-breakdown-dialog`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isIncome ? 'text-[#10B981]' : 'text-[#DC2626]'}`}>
            {isIncome ? 'Income Breakdown' : 'Expense Breakdown'}
            <span className="font-mono text-lg">{fmt(grandTotal)}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-[#52525B]">
            {isIncome
              ? 'Billed service charge across invoices + standalone Income entries (excluding auto-posted Invoice Payment rows).'
              : 'All expense entries for the selected period.'}
            {fromDate || toDate ? ` Period: ${fromDate || '—'} → ${toDate || '—'}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="border border-[#E4E4E7] rounded-xl overflow-hidden max-h-[55vh] overflow-y-auto">
          <Table>
            <TableHeader className="bg-[#F4F4F5] sticky top-0">
              <TableRow>
                <TableHead className="w-[110px] text-xs uppercase tracking-[0.12em] font-semibold text-[#52525B]">Date</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.12em] font-semibold text-[#52525B]">Category / Description</TableHead>
                <TableHead className="text-right w-[140px] text-xs uppercase tracking-[0.12em] font-semibold text-[#52525B]">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isIncome && svcCharge > 0 && (
                <TableRow className="bg-[#F0FDF4]" data-testid="row-billed-svc">
                  <TableCell className="font-mono text-xs text-[#065F46]">—</TableCell>
                  <TableCell>
                    <p className="text-sm font-semibold text-[#065F46]">Billed Service Charge</p>
                    <p className="text-[11px] text-[#15803D]">Σ (service_charge × qty) − discount across invoices in this period</p>
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold text-[#10B981]">{fmt(svcCharge)}</TableCell>
                </TableRow>
              )}
              {loading ? (
                <TableRow><TableCell colSpan={3} className="text-center text-xs text-[#A1A1AA] py-8">Loading…</TableCell></TableRow>
              ) : (rows.length === 0 && !(isIncome && svcCharge > 0)) ? (
                <TableRow><TableCell colSpan={3} className="text-center text-xs text-[#A1A1AA] py-8 italic">No {kind} entries in this period</TableCell></TableRow>
              ) : rows.map(r => (
                <TableRow key={r.id} data-testid={`row-${r.id}`}>
                  <TableCell className="font-mono text-xs text-[#52525B]">{dt(r.created_at)}</TableCell>
                  <TableCell>
                    <p className="text-sm font-semibold text-[#0A0A0A]">{r.category || '—'}</p>
                    {r.description && <p className="text-[11px] text-[#52525B] truncate max-w-[420px]">{r.description}</p>}
                  </TableCell>
                  <TableCell className={`text-right font-mono font-bold ${isIncome ? 'text-[#10B981]' : 'text-[#DC2626]'}`}>{fmt(r.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2">
          <p className="text-xs text-[#52525B]">
            {isIncome && svcCharge > 0 && (
              <span>Svc <span className="font-mono font-semibold text-[#10B981]">{fmt(svcCharge)}</span> + </span>
            )}
            {rows.length} entr{rows.length === 1 ? 'y' : 'ies'} <span className="font-mono font-semibold">{fmt(entriesTotal)}</span>
            {' = '}<span className={`font-mono font-bold ${isIncome ? 'text-[#10B981]' : 'text-[#DC2626]'}`}>{fmt(grandTotal)}</span>
          </p>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-lg" data-testid={`${kind}-breakdown-close`}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
